import { createSlice } from "@reduxjs/toolkit";

// Data Types for Dream Goal
const goalTypes = {
  TRAVEL: "Travel",
  CAR: "Car", 
  HAJJ_UMRAH: "Hajj / Umrah",
  HOUSE: "House",
  WEDDING: "Wedding",
  MOBILE: "Mobile",
  MOTORBIKE: "Motorbike",
  CUSTOM: "Create your own"
};

// Initial State with clear structure
const initialState = {
  // Current working goal data (from form submission)
  currentGoalData: null,
  
  // Temporary form data (for auto-save)
  formData: null,
  
  // All completed user goals
  userGoals: [],
  
  // UI State
  loading: false,
  error: null,
  
  // Journey state
  currentStep: 0,
  isJourneyStarted: false,
  
  // Performance optimization flags
  lastUpdateTime: null,
  updateCount: 0
};

const dreamGoalSlice = createSlice({
  name: "dreamGoal",
  initialState,
  reducers: {
    // Goal Data Management - with optimization
    setCurrentGoalData: (state, action) => {
      // Only update if data is actually different
      const newData = action.payload;
      const currentData = state.currentGoalData;
      
      // Check if data is significantly different to prevent unnecessary updates
      const isSignificantlyDifferent = !currentData || 
        currentData.goalAmount !== newData.goalAmount ||
        currentData.deadline !== newData.deadline ||
        currentData.contribution !== newData.contribution ||
        currentData.goalName !== newData.goalName ||
        currentData.goalId !== newData.goalId;
      
      if (isSignificantlyDifferent) {
        state.currentGoalData = {
          ...newData,
          updatedAt: new Date().toISOString()
        };
        state.lastUpdateTime = new Date().toISOString();
        state.updateCount += 1;
      }
    },
    
    updateCurrentGoalData: (state, action) => {
      if (state.currentGoalData) {
        const newData = action.payload;
        const currentData = state.currentGoalData;
        
        // Check if update is necessary to prevent excessive state changes
        const hasChanges = Object.keys(newData).some(key => 
          currentData[key] !== newData[key]
        );
        
        if (hasChanges) {
          state.currentGoalData = {
            ...state.currentGoalData,
            ...newData,
            updatedAt: new Date().toISOString()
          };
          state.lastUpdateTime = new Date().toISOString();
          state.updateCount += 1;
        }
      }
    },
    
    // Form Data Management - with optimization
    setFormData: (state, action) => {
      const newFormData = action.payload;
      
      // Only update if form data is actually different
      if (!state.formData || JSON.stringify(state.formData) !== JSON.stringify(newFormData)) {
        state.formData = {
          ...newFormData,
          timestamp: new Date().toISOString()
        };
        state.lastUpdateTime = new Date().toISOString();
      }
    },
    
    updateFormData: (state, action) => {
      if (state.formData) {
        const newData = action.payload;
        const currentFormData = state.formData;
        
        // Check if update is necessary
        const hasChanges = Object.keys(newData).some(key => 
          currentFormData[key] !== newData[key]
        );
        
        if (hasChanges) {
          state.formData = {
            ...state.formData,
            ...newData,
            timestamp: new Date().toISOString()
          };
          state.lastUpdateTime = new Date().toISOString();
        }
      }
    },
    
    // User Goals Management
    addUserGoal: (state, action) => {
      const newGoal = {
        ...action.payload,
        goalId: `goal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        createdAt: new Date().toISOString(),
        status: 'active'
      };
      state.userGoals.push(newGoal);
      state.lastUpdateTime = new Date().toISOString();
    },
    
    updateUserGoal: (state, action) => {
      const index = state.userGoals.findIndex((goal) => goal.goalId === action.payload.goalId);
      if (index !== -1) {
        const currentGoal = state.userGoals[index];
        const newGoalData = action.payload;
        
        // Check if update is necessary
        const hasChanges = Object.keys(newGoalData).some(key => 
          currentGoal[key] !== newGoalData[key]
        );
        
        if (hasChanges) {
          state.userGoals[index] = {
            ...currentGoal,
            ...newGoalData,
            updatedAt: new Date().toISOString()
          };
          state.lastUpdateTime = new Date().toISOString();
        }
      }
    },
    
    // Journey State Management
    setCurrentStep: (state, action) => {
      if (state.currentStep !== action.payload) {
        state.currentStep = action.payload;
        state.lastUpdateTime = new Date().toISOString();
      }
    },
    
    startJourney: (state) => {
      if (!state.isJourneyStarted || state.currentStep !== 0) {
        state.isJourneyStarted = true;
        state.currentStep = 0;
        state.lastUpdateTime = new Date().toISOString();
      }
    },
    
    // Cleanup Actions
    clearFormData: (state) => {
      if (state.formData !== null) {
        state.formData = null;
        state.lastUpdateTime = new Date().toISOString();
      }
    },
    
    clearCurrentGoalData: (state) => {
      if (state.currentGoalData !== null) {
        state.currentGoalData = null;
        state.lastUpdateTime = new Date().toISOString();
      }
    },
    
    resetJourney: (state) => {
      const hasChanges = state.currentGoalData !== null || 
                        state.formData !== null || 
                        state.currentStep !== 0 || 
                        state.isJourneyStarted !== false;
      
      if (hasChanges) {
        state.currentGoalData = null;
        state.formData = null;
        state.currentStep = 0;
        state.isJourneyStarted = false;
        state.lastUpdateTime = new Date().toISOString();
      }
    },
    
    // Utility Actions
    setLoading: (state, action) => {
      if (state.loading !== action.payload) {
        state.loading = action.payload;
        state.lastUpdateTime = new Date().toISOString();
      }
    },
    
    setError: (state, action) => {
      if (state.error !== action.payload) {
        state.error = action.payload;
        state.lastUpdateTime = new Date().toISOString();
      }
    },
    
    clearError: (state) => {
      if (state.error !== null) {
        state.error = null;
        state.lastUpdateTime = new Date().toISOString();
      }
    },
    
    // Performance optimization action
    resetUpdateCount: (state) => {
      state.updateCount = 0;
      state.lastUpdateTime = new Date().toISOString();
    },
     // Reset all dreamGoal data when user logs out
    resetDreamGoalData: (state) => {
      return {
        ...initialState
      };
    }
  },
});

// Export actions
export const {
  setCurrentGoalData,
  updateCurrentGoalData,
  setFormData,
  updateFormData,
  addUserGoal,
  updateUserGoal,
  setCurrentStep,
  startJourney,
  clearFormData,
  clearCurrentGoalData,
  resetJourney,
  setLoading,
  setError,
  clearError,
  resetUpdateCount,
  resetDreamGoalData
} = dreamGoalSlice.actions;

// Export constants
export { goalTypes };

// Export selectors with memoization hints
export const selectCurrentGoalData = (state) => state.dreamGoal.currentGoalData;
export const selectFormData = (state) => state.dreamGoal.formData;
export const selectUserGoals = (state) => state.dreamGoal.userGoals;
export const selectCurrentStep = (state) => state.dreamGoal.currentStep;
export const selectIsJourneyStarted = (state) => state.dreamGoal.isJourneyStarted;
export const selectLoading = (state) => state.dreamGoal.loading;
export const selectError = (state) => state.dreamGoal.error;
export const selectLastUpdateTime = (state) => state.dreamGoal.lastUpdateTime;
export const selectUpdateCount = (state) => state.dreamGoal.updateCount;

export default dreamGoalSlice.reducer;
